import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Account } from '../../../generated/graphql';
import { useGlobalContext } from '../../../contexts/GlobalContext';
import { getDefaultCreditAccount, getDefaultDebitAccount } from '../../../utilities/paymentUtils';
import { graphQLService } from '../../../services/graphQLService';
import { DateTime } from 'luxon';

type PaymentToaContextType = {
  debitAccount?: Account;
  setDebitAccount: (account?: Account) => void;
  creditAccount?: Account;
  setCreditAccount: (account?: Account) => void;
  paymentData: PaymentToaData;
  setPaymentData: (paymentData: PaymentToaData) => void;
  paymentDataValid: boolean;
  resetToaPayment: () => void;
  balanceInfoAmount: number | undefined;
  sendPayment: () => void;
  loading: boolean;
  navigateToVictory: boolean;
  showError: boolean;
  resetError: () => void;
};

export type PaymentToaData = {
  amount: number;
  dueDate?: DateTime;
  message?: string;
};

const defaultState: PaymentToaContextType = {
  debitAccount: undefined,
  setDebitAccount: () => {},
  creditAccount: undefined,
  setCreditAccount: () => {},
  paymentData: { amount: 0, dueDate: DateTime.now() },
  setPaymentData: () => {},
  paymentDataValid: true,
  resetToaPayment: () => {},
  balanceInfoAmount: undefined,
  sendPayment: () => {},
  loading: false,
  navigateToVictory: false,
  showError: false,
  resetError: () => {},
};

const PaymentToaContext = createContext<PaymentToaContextType>(defaultState);

export const PaymentToaProvider: FC<PropsWithChildren> = ({ children }) => {
  const globalContext = useGlobalContext();
  const { userData } = useGlobalContext();

  const [debitAccount, setDebitAccount] = useState<Account>();
  const [creditAccount, setCreditAccount] = useState<Account>();
  const [paymentData, setPaymentData] = useState<PaymentToaData>(defaultState.paymentData);
  const [paymentDataValid, setPaymentDataValid] = useState(true);
  const [balanceInfoAmount, setBalanceInfoAmount] = useState<number | undefined>(
    defaultState.balanceInfoAmount,
  );
  const [loading, setLoading] = useState(defaultState.loading);
  const [navigateToVictory, setNavigateToVictory] = useState(defaultState.navigateToVictory);
  const [showError, setShowError] = useState(defaultState.showError);

  useEffect(() => {
    const { userAccounts } = userData;
    if (userAccounts?.accounts) {
      if (!debitAccount) setDebitAccount(getDefaultDebitAccount(userAccounts.accounts));
      if (!creditAccount) setCreditAccount(getDefaultCreditAccount(userAccounts.accounts));
    }
  }, [userData, creditAccount, debitAccount]);

  useEffect(() => {
    // TODO
    setPaymentDataValid(true);
  }, [paymentData]);

  useEffect(() => {
    // TODO - handle conversion for balance info in the future
    if (debitAccount && debitAccount.currencyCode != creditAccount?.currencyCode) {
      setBalanceInfoAmount(undefined);
    } else if (debitAccount?.availableBalance && paymentData.amount) {
      setBalanceInfoAmount(debitAccount.availableBalance - paymentData.amount);
    } else {
      setBalanceInfoAmount(undefined);
    }
  }, [paymentData, debitAccount, creditAccount]);

  const resetPayment = () => {
    refreshAccounts();
    setNavigateToVictory(false);
    setLoading(false);
    resetError();
    setPaymentData(defaultState.paymentData);
    const { userAccounts } = userData;
    if (userAccounts?.accounts) {
      setDebitAccount(getDefaultDebitAccount(userAccounts.accounts));
      setCreditAccount(getDefaultCreditAccount(userAccounts.accounts));
    }
  };

  const resetError = () => {
    setShowError(false);
  };

  const sendPayment = () => {
    if (loading || !creditAccount || !debitAccount) return;
    setLoading(true);
    graphQLService
      .mutationToaPaymentOrder({
        amount: paymentData.amount,
        creditAccountId: creditAccount.id,
        debitAccountId: debitAccount.id,
        executionDate: paymentData.dueDate?.toISO().slice(0, 10),
        messageForMe: paymentData.message ?? '',
      })
      .then((response) => {
        console.debug(response.toaPaymentOrder);
        setNavigateToVictory(true);
      })
      .catch((reason) => {
        console.error(reason);
        setShowError(true);
      })
      .finally(() => {
        setLoading(false);
        refreshAccounts();
      });
  };

  const refreshAccounts = React.useCallback(() => {
    globalContext.queryUserAccounts().catch(console.error);
  }, [globalContext]);

  return (
    <PaymentToaContext.Provider
      value={{
        creditAccount: creditAccount,
        setCreditAccount: setCreditAccount,
        debitAccount: debitAccount,
        setDebitAccount: setDebitAccount,
        paymentData: paymentData,
        setPaymentData: setPaymentData,
        paymentDataValid: paymentDataValid,
        resetToaPayment: resetPayment,
        balanceInfoAmount: balanceInfoAmount,
        sendPayment: sendPayment,
        loading: loading,
        navigateToVictory: navigateToVictory,
        showError: showError,
        resetError: resetError,
      }}
    >
      {children}
    </PaymentToaContext.Provider>
  );
};

export const usePaymentToa = () => {
  const context = useContext(PaymentToaContext);

  if (!context) {
    throw new Error('usePaymentToa() must be used within an PaymentToaProvider');
  }

  return context;
};
